import {
  authSecretFromMk,
  decryptPrivateKey,
  deriveKey,
  deriveKeyWithSalt,
  encryptPrivateKey,
  encryptString,
  hashPassword,
  kekFromMk,
} from "@/lib/crypto/crypto";
import { isUserAuthenticated } from "./auth";
import { useAuthState } from "@/zustand/AuthState";
import { updateEncryptedData } from "@/server/services/recoveryKeyService";
import { getKdfParams } from "@/server/services/authService";
import { t } from "i18next";

const setDerivedKey = useAuthState.getState().setDerivedKey;
const setPrivKey = useAuthState.getState().setPrivKey;
const setEncryptedPassword = useAuthState.getState().setEncryptedPassword;
const setKdfVersion = useAuthState.getState().setKdfVersion;
const setKdfSalt = useAuthState.getState().setKdfSalt;

export const encryptDataWithNewPassword = async (
  oldPassword: string,
  password: string,
  recoveryKey: string,
) => {
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error(t("userNotAuthenticated"));
  }

  const { username, ePrK, kdfVersion, kdfSalt } = userFingerPrint;

  // OLD side: unwrap the private key with the CLIENT's CURRENT scheme — that is
  // exactly what wrapped the locally-stored ePrK, so it always unwraps here.
  const oldIsV2Plus = kdfVersion >= 2;
  const oldIsV3 = kdfVersion >= 3;
  const oldMk = oldIsV3
    ? deriveKeyWithSalt(oldPassword, kdfSalt)
    : deriveKey(oldPassword, username);
  const oldKek = oldIsV2Plus ? kekFromMk(oldMk) : oldMk;
  const pk = await decryptPrivateKey(ePrK, oldKek);

  // NEW side: derive under the SERVER's AUTHORITATIVE scheme, re-fetched now.
  // Change-password rewrites the credential + private key but does NOT rotate the
  // salt (v3 keeps its salt across a password change). If we derived under a stale
  // local scheme/salt, the stored credential would be under one salt while the row
  // records another — an unrecoverable lockout on next login (the recovery phrase
  // doesn't help; it re-derives under the row's salt too). Fetching + sending the
  // scheme keeps the row self-consistent and heals any prior local divergence.
  let srvVersion = kdfVersion;
  let srvSalt = kdfSalt;
  try {
    const p = await getKdfParams(username);
    srvVersion = p.kdf_version ?? kdfVersion;
    srvSalt = p.kdf_salt ?? kdfSalt;
  } catch {
    // Fail closed: without the authoritative scheme, writing could brick the
    // account. Abort rather than guess.
    throw new Error(t("changePassword.unableToUpdateData"));
  }
  const newIsV2Plus = srvVersion >= 2;
  const newIsV3 = srvVersion >= 3;
  const newMk = newIsV3
    ? deriveKeyWithSalt(password, srvSalt)
    : deriveKey(password, username);
  const newKek = newIsV2Plus ? kekFromMk(newMk) : newMk;
  const encryptedPrivateKey = await encryptPrivateKey(pk, newKek);

  // Recovery blob is scheme-independent: it encrypts the raw new password under a
  // key derived from the recovery phrase, so it is unchanged across v1/v2/v3.
  const passDerivedKey = deriveKey(recoveryKey, username);
  const payload = JSON.stringify({
    password: password,
  });
  const encryptedPassword = await encryptString(payload, passDerivedKey);

  // The server credential follows the account's scheme (v2/v3 authSecret vs v1 hash).
  const newCredential = newIsV2Plus
    ? authSecretFromMk(newMk)
    : hashPassword(password, username);
  try {
    await updateEncryptedData({
      password: newCredential,
      encryptedPassword: encryptedPassword,
      encryptedPrivateKey: encryptedPrivateKey,
      // Written atomically with the credential so the row's scheme can never drift.
      kdf_version: srvVersion,
      kdf_salt: srvSalt,
    });
    setDerivedKey(newKek);
    setEncryptedPassword(encryptedPassword);
    setPrivKey(encryptedPrivateKey);
    setKdfVersion(srvVersion);
    setKdfSalt(srvSalt);
  } catch (error) {
    if (error instanceof Error && error.message) {
      throw new Error(error.message);
    }
    throw new Error(t("changePassword.unableToUpdateData"));
  }
};
